'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../overlay/alert-dialog';
import { useDataManagementContext } from '../../../hooks/useDataManagementContext';
import { useErrorHandling } from '../../../utils/useErrorHandling';
import type { FieldValues } from 'react-hook-form';

export function DeleteConfirmationDialog<TData extends FieldValues>() {
  const context = useDataManagementContext<TData>();
  const { showContextError } = useErrorHandling();

  if (!context) {
    showContextError('DeleteConfirmationDialog');
    return null;
  }

  const { 
    itemToDelete, 
    onDeleteConfirm, 
    onDeleteCancel,
    title,
    isDeleting 
  } = context;

  if (!itemToDelete) {
    return null;
  }

  // Get a display name for the item (try common fields like name, title, email, etc.)
  const getItemDisplayName = (item: TData): string => {
    if ('name' in item && typeof item.name === 'string') return item.name;
    if ('title' in item && typeof item.title === 'string') return item.title;
    if ('email' in item && typeof item.email === 'string') return item.email;
    if ('id' in item && typeof item.id === 'string') return item.id;
    return 'this item';
  };

  const itemName = getItemDisplayName(itemToDelete);
  const singularTitle = title.slice(0, -1); // Remove 's' from plural title

  // Safely get the id from the item
  const itemId = 'id' in itemToDelete && typeof itemToDelete.id === 'string' 
    ? itemToDelete.id 
    : null;

  if (!itemId) {
    // If no id found, cancel the deletion
    onDeleteCancel();
    return null;
  }

  return (
    <AlertDialog open={!!itemToDelete} onOpenChange={(open) => {
      if (!open) {
        onDeleteCancel();
      }
    }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {singularTitle}?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{itemName}</strong>? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDeleteCancel} disabled={isDeleting}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onDeleteConfirm(itemId)}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
